import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState } from "react";

import { queryClient } from "@/lib/query";
import { useFetchMe } from "@/user/services";

import { useUpdatePushSubscription } from "./hooks/useUpdatePushSubscription";
import { setLocale } from "./lib/i18n";
import { router } from "./lib/router";
import { useFetchTenant } from "./user/services/tenant";

const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((module) => ({
    default: module.ReactQueryDevtools,
  })),
);

const TanStackRouterDevtools = lazy(() =>
  import("@tanstack/react-router-devtools").then((module) => ({
    default: module.TanStackRouterDevtools,
  })),
);

function InnerApp() {
  const { data: user } = useFetchMe();
  const { data: tenant } = useFetchTenant();
  const subscribe = useUpdatePushSubscription();

  useEffect(() => {
    router.invalidate();
  }, [user, tenant]);

  useEffect(() => {
    if (user) {
      subscribe();
      setLocale(user.locale);
    }
  }, [subscribe, user]);

  return <RouterProvider router={router} defaultPreload="intent" />;
}

function DevApp() {
  const [devTool, setDevTool] = useState(false);

  useEffect(() => {
    // @ts-expect-errors show dev tools only in dev mode
    window.showDevTools = () => setDevTool((prev) => !prev);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <InnerApp />
      {devTool && (
        <Suspense fallback={null}>
          <ReactQueryDevtools />
          <TanStackRouterDevtools router={router} />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}

export default function App() {
  if (process.env.NODE_ENV === "development") {
    return <DevApp />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <InnerApp />
    </QueryClientProvider>
  );
}
